import React, { useState, useRef, useEffect } from "react";
import RcSlider from "rc-slider";
import { StyledContainer } from "./styles";

/**
 * Slider component
 * @param {Object} props - The component props
 * @param {Array} props.values - The values to display as an array
 * @param {number} props.currentValue - The current value of the slider
 * @param {Function} props.onChange - The function to call when the slider value changes
 * @param {boolean} props.showDots - Whether to show the dots on the slider
 */
export default function Slider({ values, currentValue, onChange, showDots = true }) {
  // Calculate the slider's min and max based on the indices of the values array
  const sliderMin = 0;
  const sliderMax = values.length - 1;

  // Create a ref for the slider container
  const sliderContainerRef = useRef(null);

  // Find the index of the current value in the values array
  const findValueIndex = () => {
    const index = values.findIndex((item) => item.value === currentValue);
    // Return found index or default to the middle of the slider
    return index >= 0 ? index : Math.floor(sliderMax / 2);
  };

  const [sliderValue, setSliderValue] = useState(findValueIndex());
  // Track the position of the handle for the temporary label
  const [handlePosition, setHandlePosition] = useState(0);
  // Track if the slider is being interacted with (clicked/dragged) to show/hide the temporary label
  const [isInteracting, setIsInteracting] = useState(false);
  // Track if the mouse is hovering over the handle
  const [isHovering, setIsHovering] = useState(false);
  // Track if the label is at an edge position
  const [labelPosition, setLabelPosition] = useState({ left: "50%", transform: "translateX(-50%)" });
  const labelRef = useRef(null);

  const calculateHandlePosition = () => {
    if (!sliderContainerRef.current) return 0;

    const slider = sliderContainerRef.current.querySelector(".rc-slider");
    if (!slider) return 0;

    const containerWidth = slider.clientWidth;

    // Calculate position as a percentage based on the current value
    const percentage = (sliderValue - sliderMin) / (sliderMax - sliderMin);

    // Calculate position accounting for handle width
    const position = percentage * containerWidth;

    return position;
  };

  useEffect(() => {
    // Pre-calculate the position even before interaction
    const newHandlePosition = calculateHandlePosition();
    setHandlePosition(newHandlePosition);

    // Pre-position the label (it's hidden but positioned)
    if (sliderContainerRef.current) {
      const slider = sliderContainerRef.current.querySelector(".rc-slider");
      if (slider) {
        // Estimate label width since it's not visible yet
        const estimatedLabelWidth = values[sliderValue].label.length * 8 + 16; // rough estimate based on font size
        const sliderWidth = slider.clientWidth;

        let newPosition = { left: `${newHandlePosition}px`, transform: "translateX(-50%)" };

        if (newHandlePosition < estimatedLabelWidth / 2) {
          newPosition = { left: "0", transform: "none" };
        } else if (newHandlePosition > sliderWidth - estimatedLabelWidth / 2) {
          newPosition = { left: "auto", right: "0", transform: "none" };
        }

        setLabelPosition(newPosition);
      }
    }
  }, [sliderValue, values]); // Run this effect when sliderValue or values change

  // Effect to add hover detection on the slider handle
  useEffect(() => {
    if (!sliderContainerRef.current) return;

    // Function to find and attach events to the handle
    const setupHoverEvents = () => {
      const handle = sliderContainerRef.current.querySelector(".rc-slider-handle");

      if (handle) {
        const handleMouseEnter = () => setIsHovering(true);
        const handleMouseLeave = () => setIsHovering(false);

        // Touch events for mobile
        const handleTouchStart = () => setIsHovering(true);
        // For touch, we'll rely on the interaction state to hide the label on release

        // Add hover event listeners
        handle.addEventListener("mouseenter", handleMouseEnter);
        handle.addEventListener("mouseleave", handleMouseLeave);

        // Add touch event listeners
        handle.addEventListener("touchstart", handleTouchStart);

        // Return cleanup function
        return () => {
          handle.removeEventListener("mouseenter", handleMouseEnter);
          handle.removeEventListener("mouseleave", handleMouseLeave);
          handle.removeEventListener("touchstart", handleTouchStart);
        };
      }
    };

    // Short delay to ensure the handle is rendered
    const timer = setTimeout(setupHoverEvents, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [sliderValue]); // Re-attach when slider value changes, as the handle might be recreated

  // Also listen for touchend on the document to clear hover state for touch devices
  useEffect(() => {
    const handleTouchEnd = () => {
      if (isHovering) {
        setIsHovering(false);
      }
    };

    document.addEventListener("touchend", handleTouchEnd);
    return () => {
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isHovering]);

  const handleAfterChange = (index) => {
    // Call the onChange with the actual value from our values array
    onChange(values[index].value);
    // Set interacting to false when the slider handle is released
    setIsInteracting(false);
  };

  return (
    <StyledContainer>
      <div
        className="slider-container"
        ref={sliderContainerRef}
      >
        <RcSlider
          dots={showDots}
          dotStyle={{ borderColor: "transparent", width: "3.64px", height: "3.64px", backgroundColor: "var(--slider-dot)" }}
          activeDotStyle={{ backgroundColor: "var(--slider-dot-active)" }}
          handleStyle={{ width: 20, height: 20, marginTop: -6, borderColor: "transparent", boxShadow: "var(--slider-handle-drop-shadow)", opacity: 1 }}
          value={sliderValue}
          min={sliderMin}
          max={sliderMax}
          railStyle={{
            backgroundColor: "var(--slider-rail)",
            height: 7.8,
          }}
          trackStyle={{
            backgroundColor: "var(--slider-track)",
            height: 7.8,
          }}
          step={1}
          onBeforeChange={() => {
            // Just set interaction state - position is already calculated by effect
            setIsInteracting(true);
          }}
          onAfterChange={(index) => {
            // Only call onChange after releasing handle to reduce network requests
            handleAfterChange(index);
          }}
          onChange={(index) => {
            // Component requires value to get set as state to be controllable via UI
            setSliderValue(index);

            // Re-setup hover listeners on handle after any change
            // This is necessary because rc-slider may recreate the handle elements
            setTimeout(() => {
              if (sliderContainerRef.current) {
                const handle = sliderContainerRef.current.querySelector(".rc-slider-handle");

                if (handle) {
                  // If handle exists but hovering state doesn't match reality, update it
                  // This helps ensure the state remains accurate after changes
                  const isHandleHovered = handle.matches(":hover");
                  if (isHandleHovered !== isHovering) {
                    setIsHovering(isHandleHovered);
                  }
                }
              }
            }, 50);
          }}
        />
        {/* Always render the label (opacity controls visibility) */}
        <div
          className="temp-value-container"
          style={{
            ...labelPosition,
            opacity: 1 /* Always visible */,
            visibility: "visible" /* Always visible */,
          }}
        >
          <div
            className="temp-value-label"
            ref={labelRef}
          >
            {values[sliderValue].label}
          </div>
        </div>
      </div>
    </StyledContainer>
  );
}
